import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { useDocumentTemplates } from '../../core/hooks/useDocumentTemplates';
import { useDocuments } from '../../core/hooks/useDocuments';
import { useAuth } from '../../core/contexts/AuthContext';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { DocumentGenerator } from './components/DocumentGenerator';
import { DocumentStatus, type DocumentLogoSource } from '../../core/models/Document';
import type { DocumentTemplateType } from '../../core/models/Document';
import { loadTemplateContent, processTemplate } from '../../core/utils/templateLoader';
import { findClientAccountForProject, resolveProjectContactEmail } from '../../core/utils/projectContact';
import { UserRole } from '../../core/models/User';
import { useCurrentOrganization } from '../../core/hooks/useCurrentOrganization';

export const DocumentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const { templates, loading: templatesLoading, error: templatesError } = useDocumentTemplates(organizationId);
  const { createDocument } = useDocuments(null, organizationId);
  const { clients } = useClients(organizationId);
  const { organizations } = useOrganizations();
  const { organization } = useCurrentOrganization(organizationId);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplateType | null>(null);
  const [creating, setCreating] = useState(false);

  const handleGenerate = async (data: {
    title: string;
    clientId: string;
    dynamicFields: Record<string, any>;
    logoSource: DocumentLogoSource;
  }) => {
    if (!selectedTemplate || !currentUser) return;

    // Validate required fields
    if (!data.clientId) {
      alert('Please select a project.');
      return;
    }
    if (!data.title || data.title.trim() === '') {
      alert('Please enter a document title.');
      return;
    }

    // Determine effective organization for this document:
    // - For regular users: must have organizationId from auth
    // - For SUPER_ADMIN without organizationId: derive from selected client
    const selectedClient = clients.find(c => c.id === data.clientId);
    const effectiveOrganizationId =
      organizationId || (currentUser.role === UserRole.SUPER_ADMIN ? selectedClient?.organizationId : undefined);

    if (!effectiveOrganizationId) {
      alert(
        'Client ID is required. Please ensure you are logged in with a valid client account or select a project associated with a client account.'
      );
      return;
    }

    setCreating(true);
    try {
      // Load template content
      let templateContent = '';

      // Prefer content from database (template.content)
      if (selectedTemplate.content) {
        templateContent = selectedTemplate.content;
      } else if (selectedTemplate.templatePath) {
        // Fallback to loading from templatePath
        const loadedContent = await loadTemplateContent(selectedTemplate.templatePath);
        if (loadedContent) {
          templateContent = loadedContent;
        } else {
          console.warn('Template content not found at path:', selectedTemplate.templatePath);
          // Use description as fallback
          templateContent = selectedTemplate.description || '<p>Template content not available.</p>';
        }
      } else {
        // No content or path - use description as fallback
        templateContent = selectedTemplate.description || '<p>Document content will be generated from template.</p>';
      }

      // Get client and organization info for placeholders
      const clientName = selectedClient?.name || '';
      const clientAccount = findClientAccountForProject(selectedClient, organizations);
      const clientEmail = resolveProjectContactEmail(selectedClient, clientAccount);

      const orgLogoUrl = organization?.branding?.logoUrl || '';
      const clientLogoUrl = selectedClient?.logoUrl || '';

      const showOrgLogo =
        (data.logoSource === 'organization' || data.logoSource === 'both') && !!orgLogoUrl;
      const showClientLogo =
        (data.logoSource === 'client' || data.logoSource === 'both') && !!clientLogoUrl;

      const dynamicFieldsWithBranding: Record<string, any> = {
        ...(data.dynamicFields || {}),
        org_name: organization?.name || '',
        org_email: organization?.email || '',
        org_logo_url: showOrgLogo ? orgLogoUrl : '',
        client_logo_url: showClientLogo ? clientLogoUrl : '',
        show_org_logo: showOrgLogo,
        show_client_logo: showClientLogo,
      };

      // Process template content with dynamic fields
      const processedContent = processTemplate(
        templateContent,
        dynamicFieldsWithBranding,
        data.title.trim(),
        clientName,
        clientEmail
      );

      // Create document with processed template content
      const now = new Date();
      const documentData = {
        organizationId: effectiveOrganizationId,
        clientId: data.clientId,
        // Store the full template type object to satisfy DocumentTemplateType
        templateType: {
          id: selectedTemplate.id,
          name: selectedTemplate.name,
          description: selectedTemplate.description,
          templatePath: selectedTemplate.templatePath,
          content: selectedTemplate.content,
          dynamicFields: selectedTemplate.dynamicFields,
          isActive: selectedTemplate.isActive,
          organizationId: selectedTemplate.organizationId,
          createdAt: selectedTemplate.createdAt,
          updatedAt: selectedTemplate.updatedAt,
        },
        title: data.title.trim(),
        description: selectedTemplate.description || '',
        content: processedContent, // Store processed HTML content
        dynamicFields: dynamicFieldsWithBranding,
        status: DocumentStatus.DRAFT,
        version: 1,
        tags: [],
        signatures: [],
        viewedBy: [],
        logoSource: data.logoSource,
        orgLogoUrl: showOrgLogo ? orgLogoUrl : '',
        clientLogoUrl: showClientLogo ? clientLogoUrl : '',
        metadata: {
          createdBy: currentUser.uid,
          lastModifiedBy: currentUser.uid,
          createdAt: now,
          updatedAt: now,
          version: 1,
        },
      };

      await createDocument(documentData);
      navigate('/documents');
    } catch (err: any) {
      console.error('Error creating document:', err);
      let errorMessage = 'Failed to create document. Please try again.';
      if (err?.code === 'permission-denied') {
        errorMessage = 'You do not have permission to create documents for this project. Please ensure you have access to the selected project.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = 'Operation failed. This might be due to missing database indexes. Please wait a few minutes and try again, or contact support.';
      } else if (err?.code === 'not-found') {
        errorMessage = 'The selected project was not found. Please select a different project.';
      } else if (err?.message) {
        errorMessage = err.message;
      }
      alert(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  if (templatesLoading) {
    return <LoadingState message="Loading templates..." />;
  }

  if (templatesError) {
    return <ErrorState message="Failed to load templates" />;
  }

  if (!selectedTemplate) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Create Document"
          description="Select a template to generate a new document"
          icon={FileText}
          actions={
            <Button variant="outline" onClick={() => navigate('/documents')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          }
        />

        <Card>
          <CardHeader>
            <CardTitle>Select Template</CardTitle>
            <CardDescription>Choose a document template to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Select
                onValueChange={(value) => {
                  const template = templates.find(t => t.id === value);
                  if (template) setSelectedTemplate(template);
                }}
                disabled={templatesLoading || templates.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      templatesLoading
                        ? "Loading templates..."
                        : templates.length === 0
                        ? "No templates available"
                        : "Select a template"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.isActive && t.content).length > 0 ? (
                    templates.filter(t => t.isActive && t.content).map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-gray-500">
                      No active templates available
                    </div>
                  )}
                </SelectContent>
              </Select>

              {templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No templates available. Create a template first.</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate('/documents/templates/new')}
                  >
                    Create Template
                  </Button>
                </div>
              ) : templates.filter(t => t.isActive).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No active templates available. Activate an existing template or create a new one.</p>
                  <div className="flex gap-2 justify-center mt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/documents/templates')}
                    >
                      Manage Templates
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/documents/templates/new')}
                    >
                      Create Template
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Document"
        description={`Generating document from template: ${selectedTemplate.name}`}
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Templates
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6">
          <DocumentGenerator
            template={selectedTemplate}
            onGenerate={handleGenerate}
            onCancel={() => setSelectedTemplate(null)}
            isSubmitting={creating}
          />
        </CardContent>
      </Card>
    </div>
  );
};
